/**
 * ProductBottlesCombined — Custom element for 2-product color-combined bottle pages.
 * Swaps between two single-variant Shopify products via a Black/White pill picker.
 * Updates the hidden variant id, price, compare-at price, and Add-to-Cart button state.
 *
 * Layout: single Color fieldset (Black | White). No size. No age toggle. No media swap.
 */

class ProductBottlesCombined extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset.section;

    this.blackVariants = this.parseJSON('[data-black-variants]');
    this.whiteVariants = this.parseJSON('[data-white-variants]');

    // Pre-formatted prices keyed by variant id, rendered server-side in the
    // visitor's market currency (e.g. €26,95 in Spain). Used as the only
    // source of price strings — JS never formats money itself.
    this.priceMap = Object.assign(
      {},
      this.parseJSON('[data-black-prices]') || {},
      this.parseJSON('[data-white-prices]') || {}
    );

    this.priceEls = this.querySelectorAll('[data-price]');
    this.comparePriceEl = this.querySelector('[data-compare-price]');
    this.variantIdInput = this.querySelector('[data-variant-id]');
    this.submitText = this.querySelector('[data-add-to-cart-text]');
    this.submitBtn = this.querySelector('#ProductSubmitButton-' + this.sectionId);

    this.colorFieldset = this.querySelector('fieldset[data-option-name="color"]');
  }

  connectedCallback() {
    this.bindColorPicker();
    this.updateSelectedVariant();
  }

  parseJSON(selector) {
    const el = this.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('ProductBottlesCombined: failed to parse JSON for', selector, e);
      return null;
    }
  }

  bindColorPicker() {
    if (!this.colorFieldset) return;
    this.colorFieldset.addEventListener('change', (e) => {
      if (e.target.type !== 'radio') return;
      this.updateSelectedDisplay(e.target);
      this.updateSelectedVariant();
    });
  }

  updateSelectedDisplay(input) {
    const selectedDisplay = this.colorFieldset.querySelector('[data-selected-value]');
    if (selectedDisplay) {
      const label = this.colorFieldset.querySelector('label[for="' + input.id + '"]');
      selectedDisplay.textContent = label ? label.textContent.trim() : input.value;
    }
  }

  getSelectedColor() {
    if (!this.colorFieldset) return null;
    const checked = this.colorFieldset.querySelector('input[type="radio"]:checked');
    return checked ? checked.value : null;
  }

  getActiveVariants() {
    const color = this.getSelectedColor();
    if (color === 'black') return this.blackVariants;
    if (color === 'white') return this.whiteVariants;
    return null;
  }

  resolveVariant(variants) {
    if (!variants || !variants.length) return null;
    const available = variants.find((v) => v.available);
    return available || variants[0];
  }

  updateSelectedVariant() {
    const variant = this.resolveVariant(this.getActiveVariants());

    if (variant && this.variantIdInput) {
      this.variantIdInput.value = variant.id;
      this.variantIdInput.disabled = !variant.available;
    } else if (this.variantIdInput) {
      this.variantIdInput.disabled = true;
    }

    this.updatePrice(variant);
    this.updateButton(variant);
    this.updateHeroImage(variant);
  }

  updateHeroImage(variant) {
    if (window.RBHeroImage && typeof window.RBHeroImage.swap === 'function') {
      window.RBHeroImage.swap(this, variant);
    }
  }

  updatePrice(variant) {
    if (!this.priceEls || !this.priceEls.length) return;

    if (!variant) {
      this.priceEls.forEach((el) => { el.textContent = ''; });
      return;
    }

    const entry = this.priceMap[variant.id];
    if (!entry) return;

    this.priceEls.forEach((el) => { el.innerHTML = entry.price; });

    const priceContainer = this.querySelector('#price-' + this.sectionId);
    if (!priceContainer) return;

    const saleEl = priceContainer.querySelector('.price__sale');
    const regularEl = priceContainer.querySelector('.price__regular');

    if (entry.compare) {
      if (this.comparePriceEl) {
        this.comparePriceEl.innerHTML = entry.compare;
      }
      if (saleEl) saleEl.style.display = '';
      if (regularEl) regularEl.style.display = 'none';
      priceContainer.classList.add('price--on-sale');
    } else {
      if (saleEl) saleEl.style.display = 'none';
      if (regularEl) regularEl.style.display = '';
      priceContainer.classList.remove('price--on-sale');
    }
  }

  updateButton(variant) {
    if (!this.submitBtn || !this.submitText) return;

    if (!variant) {
      this.submitBtn.disabled = true;
      this.submitText.textContent = 'Unavailable';
    } else if (!variant.available) {
      this.submitBtn.disabled = true;
      this.submitText.textContent = 'Sold out';
    } else {
      this.submitBtn.disabled = false;
      this.submitText.textContent = 'Add to cart';
    }
  }
}

customElements.define('product-bottles-combined', ProductBottlesCombined);
